import {
    FC,
    FormEvent,
    KeyboardEvent,
    ReactElement,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import classNames from 'classnames'

import { Textarea } from '../../../components'
import { FormActionButtons, SegmentTabs } from '../../../compositions'
import { SegmentTabOption } from '../../../compositions/SegmentTabs/SegmentTabs'
import {
    ValkTalkFeedType,
    ValkTalkPost,
    ValkTalkPostFormData,
    ValkTalkPostImage,
} from '../../../entities/ValkTalkPost/ValkTalkPost'
import { hasValkTalkPostContent, moveArrayElement } from '../../../entities/ValkTalkPost/ValkTalkPostHelpers'
import trans from '../../../helpers/trans'
import { ValkTalkPostImagesEditor } from './subcomponents'
import {
    validateValkTalkPostFormData,
    validateValkTalkPostImageFile,
    ValkTalkPostFormErrors,
} from './validations/valkTalkPostFormValidation'

import './ValkTalkPostForm.scss'

interface ValkTalkPostFormProps {
    isLoading: boolean
    authorName: string
    permittedFeedTypes: ValkTalkFeedType[]
    post?: ValkTalkPost
    onSubmit: (formData: ValkTalkPostFormData) => void
    onCancel: () => void
    className?: string
}

let localImageCount = 0

const createLocalImage = (file: File): ValkTalkPostImage => {
    localImageCount += 1

    return {
        id: `local-${localImageCount}-${file.name}`,
        url: URL.createObjectURL(file),
        file,
    }
}

export const ValkTalkPostForm: FC<ValkTalkPostFormProps> = ({
    isLoading,
    authorName,
    permittedFeedTypes,
    post,
    onSubmit,
    onCancel,
    className = '',
}): ReactElement => {
    const isEditing = !! post

    const [formData, setFormData] = useState<ValkTalkPostFormData>({
        feedType: post?.feedType || permittedFeedTypes[0],
        title: post?.title || '',
        text: post?.text || '',
        images: post?.images || [],
    })
    const [formErrors, setFormErrors] = useState<ValkTalkPostFormErrors>({})
    const [imageErrors, setImageErrors] = useState<string[]>([])

    const objectUrls = useRef<Set<string>>(new Set())

    const isAnnouncement = formData.feedType === ValkTalkFeedType.valk
    const showFeedTypeTabs = permittedFeedTypes.length > 1 && ! isEditing

    const feedTypeOptions = useMemo((): SegmentTabOption<ValkTalkFeedType>[] => permittedFeedTypes.map(feedType => ({
        value: feedType,
        label: trans(`types.valkTalkFeedType.${feedType}.label`),
        description: trans(`types.valkTalkFeedType.${feedType}.description`),
    })), [permittedFeedTypes])

    useEffect(() => () => {
        objectUrls.current.forEach(url => URL.revokeObjectURL(url))
    }, [])

    const updateFormData = (newFormData: Partial<ValkTalkPostFormData>): void => {
        const updatedFormData = { ...formData, ...newFormData }

        setFormData(updatedFormData)

        if (formErrors.content && hasValkTalkPostContent(updatedFormData)) {
            setFormErrors({ ...formErrors, content: undefined })
        }
    }

    const handleAddImages = (files: File[]): void => {
        const errors = files.map(validateValkTalkPostImageFile).filter((error): error is string => !! error)
        const newImages = files
            .filter(file => ! validateValkTalkPostImageFile(file))
            .map(createLocalImage)

        newImages.forEach(image => objectUrls.current.add(image.url))

        setImageErrors(errors)
        updateFormData({ images: [...formData.images, ...newImages] })
    }

    const handleRemoveImage = (imageId: string): void => {
        const image = formData.images.find(formImage => formImage.id === imageId)

        if (image && objectUrls.current.has(image.url)) {
            URL.revokeObjectURL(image.url)
            objectUrls.current.delete(image.url)
        }

        updateFormData({ images: formData.images.filter(formImage => formImage.id !== imageId) })
    }

    const handleMoveImage = (fromIndex: number, toIndex: number): void => {
        updateFormData({ images: moveArrayElement(formData.images, fromIndex, toIndex) })
    }

    // Prevent accidental form submits when pressing enter key (e.g. when inside the title input)
    const handleKeyDown = (event: KeyboardEvent<HTMLFormElement>): void => {
        const { target } = event

        if (event.key === 'Enter' && target instanceof HTMLInputElement && target.type !== 'file') {
            event.preventDefault()
        }
    }

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        const [errors, hasErrors] = validateValkTalkPostFormData(formData)
        setFormErrors(errors)

        if (! hasErrors) {
            onSubmit(formData)
        }
    }

    const cardClassName = classNames('valk-talk-post-form__card', {
        'valk-talk-post-form__card--is-announcement': isAnnouncement,
    })

    return (
        <form onSubmit={handleSubmit} onKeyDown={handleKeyDown} className={`valk-talk-post-form ${className}`}>
            {showFeedTypeTabs && (
                <SegmentTabs
                    name="valk-talk-feed-type"
                    label={trans('containers.forms.valkTalkPostForm.feedType')}
                    options={feedTypeOptions}
                    value={formData.feedType}
                    disabled={isLoading}
                    onChange={feedType => updateFormData({ feedType })}
                    className="valk-talk-post-form__feed-type-tabs"
                />
            )}

            {! showFeedTypeTabs && (
                <p className="valk-talk-post-form__feed-type">
                    {trans('containers.forms.valkTalkPostForm.postingIn', {
                        feed: trans(`types.valkTalkFeedType.${formData.feedType}.label`),
                    })}
                </p>
            )}

            <p className="valk-talk-post-form__preview-hint">
                {trans('containers.forms.valkTalkPostForm.previewHint')}
            </p>

            <article className={cardClassName}>
                <header className="valk-talk-post-form__card-header">
                    <span className="valk-talk-post-form__avatar" aria-hidden>
                        {authorName.charAt(0)}
                    </span>
                    <div className="valk-talk-post-form__meta">
                        <span className="valk-talk-post-form__author">{authorName}</span>
                        <span className="valk-talk-post-form__time">
                            {trans('containers.forms.valkTalkPostForm.justNow')}
                        </span>
                    </div>
                </header>

                <div className="valk-talk-post-form__content">
                    <input
                        type="text"
                        value={formData.title}
                        placeholder={trans('containers.forms.valkTalkPostForm.titlePlaceholder')}
                        aria-label={trans('containers.forms.valkTalkPostForm.title')}
                        disabled={isLoading}
                        onChange={event => updateFormData({ title: event.target.value })}
                        className="valk-talk-post-form__title-input"
                    />

                    <Textarea
                        autoGrow
                        value={formData.text}
                        placeholder={trans('containers.forms.valkTalkPostForm.textPlaceholder')}
                        aria-label={trans('containers.forms.valkTalkPostForm.text')}
                        disabled={isLoading}
                        onChange={event => updateFormData({ text: event.target.value })}
                        className="valk-talk-post-form__text-input"
                    />

                    {!! formData.images.length && (
                        <div className="valk-talk-post-form__image-preview">
                            <img src={formData.images[0].url} alt="" className="valk-talk-post-form__image" />
                            {formData.images.length > 1 && (
                                <span className="valk-talk-post-form__image-count">
                                    {`+${formData.images.length - 1}`}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </article>

            {formErrors.content && (
                <p className="valk-talk-post-form__error" role="alert">{formErrors.content}</p>
            )}

            <ValkTalkPostImagesEditor
                images={formData.images}
                disabled={isLoading}
                errors={imageErrors}
                onAdd={handleAddImages}
                onMove={handleMoveImage}
                onRemove={handleRemoveImage}
                className="valk-talk-post-form__images-editor"
            />

            <FormActionButtons
                isLoading={isLoading}
                submitText={isEditing
                    ? trans('containers.forms.valkTalkPostForm.save')
                    : trans('containers.forms.valkTalkPostForm.publish')}
                onCancelClick={onCancel}
            />
        </form>
    )
}
